import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import type { TooltipContextValue, TooltipPlacement } from './types';

const TooltipContext = createContext<TooltipContextValue | null>(null);

interface TooltipProviderProps {
  children: ReactNode;
  placement: TooltipPlacement;
  delayShow?: number;
  delayHide?: number;
}

export function TooltipProvider({
  children,
  placement,
  delayShow = 0,
  delayHide = 0,
}: TooltipProviderProps) {
  const timeoutMap = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const [isVisible, setIsVisible] = useState(false);
  const [triggerLayout, setTriggerLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const showTooltip = useCallback(() => {
    if (delayShow > 0) {
      const timeout = setTimeout(() => {
        setIsVisible(true);
        timeoutMap.current.delete('show');
      }, delayShow);

      timeoutMap.current.set('show', timeout);
    } else {
      setIsVisible(true);
    }
  }, [delayShow]);

  const hideTooltip = useCallback(() => {
    if (delayHide > 0) {
      const timeout = setTimeout(() => {
        setIsVisible(false);
        timeoutMap.current.delete('hide');
      }, delayHide);
      timeoutMap.current.set('hide', timeout);
    } else {
      setIsVisible(false);
    }
  }, [delayHide]);

  const contextValue: TooltipContextValue = {
    isVisible,
    showTooltip,
    hideTooltip,
    triggerLayout,
    setTriggerLayout,
    placement,
  };

  useEffect(() => {
    const map = timeoutMap.current;

    // Remove timeouts on component unmount to prevent memory leaks
    return () => {
      Array.from(map.entries()).forEach(([_, timeout]) =>
        clearTimeout(timeout),
      );
    };
  }, []);

  return (
    <TooltipContext.Provider value={contextValue}>
      {children}
    </TooltipContext.Provider>
  );
}

export function useTooltipContext(): TooltipContextValue {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error(
      'Tooltip components must be used within a Tooltip provider',
    );
  }
  return context;
}
